import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

/**
 * What Shopify → Fees (`pages/ShopifyFees.tsx`) shows of `shopify.usageCharges`: the words for
 * each charge status and the per-status totals, zero-filled, kept out of the page so they are
 * tested.
 */

type UsageCharges = inferOutput<TRPCOptionsProxy<AppRouter>["shopify"]["usageCharges"]>;

/** One usage charge row as the dashboard API lists it (`listUsageCharges`). */
export type UsageChargeRow = UsageCharges["charges"][number];
export type UsageChargeStatus = UsageChargeRow["status"];

/** The statuses in the order the page's totals show them: billed first, then what is not. */
export const USAGE_CHARGE_STATUSES: readonly UsageChargeStatus[] = [
  "applied",
  "queued",
  "failed",
  "skipped",
];

/** Each status's label on the page, and what it means, shown on hover. */
export const USAGE_CHARGE_STATUS_TEXT: Record<UsageChargeStatus, { label: string; hint: string }> =
  {
    applied: {
      label: "Billed",
      hint: "Shopify accepted the usage record; it is on the merchant's Shopify bill",
    },
    queued: {
      label: "Queued",
      hint: "Not billed yet; retried every five minutes",
    },
    failed: {
      label: "Failed",
      hint: "Shopify refused it ten times; it is no longer retried",
    },
    skipped: {
      label: "Not billed",
      hint: "Nothing was billed: no active plan when the box settled, or the fee rounded to zero",
    },
  };

export interface UsageChargeTotal {
  status: UsageChargeStatus;
  count: number;
  amountCents: number;
  volumeCents: number;
}

/** One total per status, in `USAGE_CHARGE_STATUSES` order, zero for a status with no rows. */
export function usageChargeTotals(
  rows: readonly UsageChargeTotal[] | undefined,
): UsageChargeTotal[] {
  return USAGE_CHARGE_STATUSES.map(
    (status) =>
      rows?.find((row) => row.status === status) ?? {
        status,
        count: 0,
        amountCents: 0,
        volumeCents: 0,
      },
  );
}
